import { Link as RouterLink } from 'react-router-dom';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { brand } from '../../themes/howdenTheme';
import {
  AVAILABILITY_LABEL,
  CAPABILITY_AREAS,
  capabilitiesInArea,
} from '../../modules/capabilities/capabilities';
import type { DataSphereCapability } from '../../modules/capabilities/capabilities';
import { serviceHref } from './ServiceShell';

const ADO_WORK_ITEM =
  'https://dev.azure.com/HyperionDataServices/GDL-Hub%20spoke/_workitems/edit/';

const useStyles = makeStyles(theme => ({
  status: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    backgroundColor: brand.accent,
    color: brand.onAccent,
    fontWeight: 600,
    fontSize: 14,
  },
  title: {
    color: brand.primary,
    margin: theme.spacing(2, 0, 0),
  },
  rule: {
    width: theme.spacing(7),
    height: 4,
    border: 0,
    margin: theme.spacing(2, 0, 3),
    backgroundColor: brand.rule,
  },
  lead: {
    fontSize: 18,
    color: brand.ink,
    maxWidth: '64ch',
  },
  panel: {
    marginTop: theme.spacing(5),
    padding: theme.spacing(4),
    border: `1px solid ${brand.border}`,
    backgroundColor: brand.surface,
    maxWidth: 880,
  },
  panelTitle: {
    color: brand.ink,
    marginBottom: theme.spacing(2),
  },
  body: {
    color: brand.ink,
    maxWidth: '64ch',
  },
  stories: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    listStyle: 'none',
    margin: theme.spacing(2, 0, 0),
    padding: 0,
    '& a': {
      display: 'inline-block',
      padding: theme.spacing(0.5, 1.5),
      border: `1px solid ${brand.border}`,
      color: brand.primary,
      fontWeight: 600,
      textDecoration: 'none',
      '&:hover': { backgroundColor: brand.subtle },
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
}));

/**
 * Placeholder for a roadmap service that is not built yet. It says what the
 * service will do, when it is planned, where the work is tracked and what to
 * do in the meantime, so the journey does not end at a dead link.
 */
export const PlannedServicePage = ({
  capability,
}: {
  capability: DataSphereCapability;
}) => {
  const classes = useStyles();
  const area = CAPABILITY_AREAS.find(a => a.id === capability.area);
  const liveInArea = capabilitiesInArea(capability.area).filter(
    c => c.availability === 'live',
  );

  return (
    <article aria-labelledby="planned-title">
      <span className={classes.status}>
        <ScheduleIcon fontSize="small" aria-hidden />
        {AVAILABILITY_LABEL[capability.availability]}
      </span>
      <Typography id="planned-title" variant="h2" className={classes.title}>
        {capability.title}
      </Typography>
      <hr className={classes.rule} aria-hidden="true" />
      <Typography className={classes.lead}>{capability.description}</Typography>

      <section className={classes.panel} aria-labelledby="planned-now">
        <Typography
          id="planned-now"
          variant="h5"
          component="h3"
          className={classes.panelTitle}
        >
          Need this before it is available?
        </Typography>
        <Typography variant="body1" className={classes.body}>
          Raise a request with the Service Desk and attach the Platform
          Engineering engagement form. A platform engineer will review it with
          you and agree a delivery date.
        </Typography>
        {liveInArea.length > 0 ? (
          <>
            <Typography
              variant="body1"
              className={classes.body}
              style={{ marginTop: 16 }}
            >
              These services in {area?.title ?? 'this area'} are available now.
            </Typography>
            <div className={classes.actions}>
              {liveInArea.map(live => (
                <Button
                  key={live.id}
                  component={RouterLink}
                  to={serviceHref(live)}
                  variant="outlined"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                >
                  {live.title}
                </Button>
              ))}
            </div>
          </>
        ) : null}
      </section>

      {capability.roadmapIds.length > 0 ? (
        <section className={classes.panel} aria-labelledby="planned-roadmap">
          <Typography
            id="planned-roadmap"
            variant="h5"
            component="h3"
            className={classes.panelTitle}
          >
            On the platform roadmap
          </Typography>
          <Typography variant="body1" className={classes.body}>
            The work for this service is tracked in these roadmap items.
          </Typography>
          <ul className={classes.stories}>
            {capability.roadmapIds.map(id => (
              <li key={id}>
                <a
                  href={`${ADO_WORK_ITEM}${id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  #{id}
                </a>
              </li>
            ))}
          </ul>
          <div className={classes.actions}>
            <Button
              component={RouterLink}
              to="/roadmap"
              variant="contained"
              color="primary"
            >
              Open the roadmap
            </Button>
          </div>
        </section>
      ) : null}
    </article>
  );
};
